import { useEffect } from "react";
import { Sentry } from "./sentry";

/**
 * Small hook the Shop routes call on mount so Sentry breadcrumbs and tags
 * make it obvious which shop surface a user was on when things went sideways
 * (the transient HMR `useState`-of-null blip is the motivating case).
 *
 * We set two tags:
 *   - `route`      = "shop"              (broad filter: "any shop page")
 *   - `shop.view`  = "index" | "product" (narrows to landing vs. detail)
 *
 * And drop a breadcrumb with the productId when applicable so the
 * pre-crash trail in a Sentry issue reads "user landed on shop → opened
 * product tee-navy → crashed" without ambiguity.
 */
export function useShopSentry(
  view: "index" | "product",
  extra?: { productId?: string; productName?: string },
) {
  useEffect(() => {
    Sentry.setTag("route", "shop");
    Sentry.setTag("shop.view", view);
    if (extra?.productId) {
      Sentry.setTag("shop.product_id", extra.productId);
    }
    Sentry.addBreadcrumb({
      category: "shop",
      type: "navigation",
      level: "info",
      message:
        view === "product"
          ? `Mounted shop product page: ${extra?.productName ?? extra?.productId ?? "unknown"}`
          : "Mounted shop landing",
      data: {
        view,
        productId: extra?.productId,
        productName: extra?.productName,
      },
    });
    return () => {
      // Don't leave stale tags on the scope after leaving the shop.
      Sentry.setTag("shop.view", undefined);
      Sentry.setTag("shop.product_id", undefined);
    };
  }, [view, extra?.productId, extra?.productName]);
}

/**
 * Report a shop route render/loader failure to Sentry with a stable
 * `boundary` tag matching what the app-root `SentryBoundary` uses, so all
 * shop-side errors group under one issue lens.
 */
export function reportShopRouteError(
  error: unknown,
  context: { view: "index" | "product"; productId?: string },
) {
  Sentry.withScope((scope) => {
    scope.setTag("boundary", `route_shop_${context.view}`);
    scope.setTag("route", "shop");
    scope.setTag("shop.view", context.view);
    if (context.productId) scope.setTag("shop.product_id", context.productId);
    scope.setLevel("error");
    Sentry.captureException(error);
  });
}
